<template>
    <div class="row justify-content-center">
        <transition name="fade" mode="out-in">
            <AppPlaceholder class="col-xl-8" v-if="!product" key="no-product"/>
            <div v-else class="col-xl-8" key="product">
                <div class="row">
                    <div class="col-12 mb-4 d-sm-flex justify-content-between align-items-baseline">
                        <div class="pl-3">
                            <h4>New {{ productType }}</h4>
                            <div v-if="!isNew">
                                <p class="mb-0 text-muted">
                                    <small>Created on {{ product.created_at | date }}</small>
                                </p>
                            </div>
                        </div>
                        <div class="col-12 col-sm-auto text-right pr-0 mt-2 mt-sm-0">
                            <button class="btn btn-primary mr-2"
                                    :disabled="loading"
                                    @click="save">
                                Save
                            </button>
                            <button class="btn btn-outline-secondary"
                                    :disabled="loading"
                                    @click="saveAndClose">
                                Save & close
                            </button>
                            <b-dropdown variant="link"
                                        no-caret
                                        right
                                        v-if="!isNew">
                                <template v-slot:button-content>
                                    <i class="material-icons">more_vert</i>
                                </template>
                                <b-dropdown-item :disabled="loading"
                                                 @click="destroy">
                                    Delete
                                </b-dropdown-item>
                            </b-dropdown>
                        </div>
                    </div>
                </div>
                <div class="card">
                    <div class="card-body">
                        <div class="row">
                            <AppInput label="Name"
                                      :value="product.name"
                                      field="name"
                                      :errors="errors"
                                      @change="updateProductProps({ name: $event })"
                                      class="col-md-6"/>
                            <AppInput label="Density (kg/dm³)"
                                      :value="product.density"
                                      field="density"
                                      :errors="errors"
                                      @change="updateProductProps({ density: $event })"
                                      class="col-md-4"
                                      v-if="isProduct"/>
                        </div>
                        <div class="row">
                            <AppSelect :value="product.types || null"
                                       :multiple="true"
                                       :options="productTypes"
                                       track-by="id"
                                       label-field="name"
                                       label="Type"
                                       placeholder=""
                                       @search-change="suggestProductTypes($event)"
                                       @input="updateProductProps({
                                       type_id: $event ? $event[0].id : null,
                                       types: $event })"
                                       class="col-md-5"/>
                            <div class="col-lg-5 mb-3 mb-lg-0">
                                <label>Visibility in orders</label>
                                <AppCheckbox :errors="errors"
                                             label="Is hidden"
                                             :value="product.is_hidden"
                                             @input="updateProductProps({is_hidden: $event})"
                                             field="is_hidden"/>
                            </div>
                            <AppSelect :value="product.supplier_company"
                                       :errors="errors"
                                       :options="companies"
                                       :allow-empty="true"
                                       field="supplier_company_id"
                                       track-by="id"
                                       label-field="name"
                                       label="Supplier"
                                       placeholder=""
                                       @search-change="suggestCompaniesByType($event, 'supplier')"
                                       @input="updateProductProps({
                                        supplier_company_id: $event ? $event.id : null,
                                        supplier_company: $event,
                                        })"
                                       class="col-lg-4 col-md-6"/>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>
<script>
import { mapGetters } from 'vuex';
import debounce from 'lodash.debounce';
import { BDropdown, BDropdownItem } from 'bootstrap-vue';
import NotificationService from '../../services/notification.service';
import AppInput from '../form/AppInput';
import AppPlaceholder from '../form/AppPlaceholder';
import AppSelect from '../form/AppSelect';
import AppCheckbox from '../form/AppCheckbox';
import Errors from '../../utils/errors';
import { formatDate } from '../../filters/date.filter';

export default {
  components: {
    AppInput,
    AppPlaceholder,
    AppSelect,
    AppCheckbox,
    BDropdown,
    BDropdownItem,
  },
  data() {
    return {
      companies: [],
      productTypes: [],
      errors: new Errors(),
      loading: false,
      canClose: false,
    };
  },
  filters: {
    date: formatDate,
  },
  computed: {
    ...mapGetters({
      product: 'products/product',
    }),
    isNew() {
      return this.$route.params.id === 'new';
    },
    productType() {
      return this.$route.params.productType;
    },
    isProduct() {
      return this.productType === 'product';
    },
  },
  mounted() {
    this.initProduct();
  },
  watch: {
    '$route.params.id'() {
      this.initProduct();
    },
  },
  methods: {
    initProduct() {
      if (this.isNew) {
        this.$store.dispatch('products/newProduct');
        if (this.$route.params.productType === 'service') {
          this.updateProductProps({
            type_id: 'service',
            types: [
              {
                created_at: null,
                id: 'service',
                name: 'service',
                update_at: null,
              },
            ],
          });
        }
      } else {
        this.getProduct();
      }
    },
    suggestCompaniesByType: debounce(function suggest(query, type) {
      const params = {
        query,
        type: type,
      };
      return this.$store.dispatch('companies/suggestCompanies', params)
        .then((res) => {
          this.companies = res;
        });
    }, 300),
    suggestProductTypes: debounce(function suggest(query) {
      const params = {
        query,
      };
      return this.$store.dispatch('productTypes/suggestProductTypes', params)
        .then((res) => {
          this.productTypes = res;
        });
    }, 300),
    getProduct() {
      this.$store.dispatch('products/getProduct', this.$route.params.id);
    },
    updateProductProps(props) {
      this.$store.dispatch('products/updateProductProps', props);
    },
    saveAndClose() {
      this.save()
        .then(() => {
          if (this.canClose) {
            this.$router.push({
              name: 'products',
              params: { productType: this.productType },
            });
          }
        });
    },
    save() {
      this.loading = true;
      this.errors.clear();
      return this.$store
        .dispatch(this.isNew ? 'products/createProduct' : 'products/updateProduct')
        .then((res) => {
          this.canClose = true;
          if (this.isNew) {
            this.$router.push({
              name: 'product',
              params: { id: res.product_id },
            });
            NotificationService.success(res.message);
          }
        })
        .catch((err) => {
          this.canClose = false;
          this.errors.set(err.response.data.errors);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    destroy() {
      this.$bvModal
        .msgBoxConfirm('Delete product?', {
          okTitle: 'Delete',
          okVariant: 'danger',
        })
        .then((value) => {
          if (value) {
            this.$store.dispatch('products/deleteProduct')
              .then((res) => {
                NotificationService.success(res.message);
                this.$router.push({ name: 'products' });
              });
          }
        });
    },
  },
};
</script>
